import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogImageAlt?: string;
  ogType?: string;
  keywords?: string;
  jsonLd?: object | object[];
  noindex?: boolean;
  publishedTime?: string;
  modifiedTime?: string;
}

const BASE_URL = "https://www.paradoxedge.com";
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png?v=2`;
const BRAND = "ParadoxEdge";

const SEOHead = ({
  title = "ParadoxEdge | Executive Coaching & Leadership Development",
  description = "Executive coaching and leadership development for leaders ready to grow through continuous practice. Denver-based, working globally.",
  canonicalUrl,
  ogImage = DEFAULT_OG_IMAGE,
  ogImageAlt = "ParadoxEdge — Executive Coaching & Leadership Development",
  ogType = "website",
  keywords = "executive coaching, leadership development, professional coaching, organizational development, vertical development, leadership training",
  jsonLd,
  noindex = false,
  publishedTime,
  modifiedTime,
}: SEOHeadProps) => {
  const fullCanonicalUrl = canonicalUrl ? `${BASE_URL}${canonicalUrl}` : BASE_URL;
  const fullTitle = title.includes(BRAND) || title.includes("Paradox Edge")
    ? title
    : `${title} | ${BRAND}`;

  // Trim description defensively to keep under SERP limits
  const safeDescription = description.length > 160
    ? description.slice(0, 157).trimEnd() + "…"
    : description;

  const defaultJsonLd = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": BRAND,
    "description": safeDescription,
    "url": fullCanonicalUrl,
    "logo": `${BASE_URL}/lovable-uploads/180210a9-9a73-4038-a36b-f373598ef48a.png`,
    "image": ogImage,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Denver",
      "addressRegion": "CO",
      "addressCountry": "US"
    },
    "areaServed": "Worldwide",
    "priceRange": "$$$$",
    "serviceType": [
      "Executive Coaching",
      "Leadership Development",
      "Organizational Development"
    ],
    "sameAs": [
      "https://www.linkedin.com/company/90481320",
      "https://www.linkedin.com/in/kent-frazier/"
    ]
  };

  const renderedJsonLd = jsonLd ?? defaultJsonLd;
  const jsonLdArray = Array.isArray(renderedJsonLd) ? renderedJsonLd : [renderedJsonLd];

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={safeDescription} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={BRAND} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1"} />
      <meta name="googlebot" content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1"} />
      <meta name="theme-color" content="#C45C26" />

      {/* Canonical URL */}
      <link rel="canonical" href={fullCanonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={safeDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={BRAND} />
      <meta property="og:locale" content="en_US" />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@paradoxedge" />
      <meta name="twitter:creator" content="@paradoxedge" />
      <meta name="twitter:url" content={fullCanonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={safeDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={ogImageAlt} />

      {/* JSON-LD Structured Data */}
      {jsonLdArray.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOHead;
